import React, { useContext } from 'react';
import MMMListViewContext from './MMMListViewContext';
import { useHistory } from 'react-router-dom';
import { MMMTYPE } from '../../../../Utilities/NamingConversions';
import AuthContext from '@context/auth/AuthContext';
import MMMContext from '@context/MMM/MMMContext';
import MainContext from '@context/main/MainContext';
import FoldersContext from '@context/folder/FoldersContext/FoldersContext';
import { DASHBOARD_ROUTES } from '../../../../Utilities/routePaths';
import { mmmRootParentPath } from '../../constants';
import { deleteTargetItems, updateModelStatus } from '@utils/mmmListViewUtils';
import { MMMService } from '@api/mmm';
import { FolderService } from '@api/folder';
import { SDMMMService } from '../../../../../api/sdmmm';

// eslint-disable-next-line react/prop-types
const MMMListViewProvider = ({ children }) => {
  const history = useHistory();
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const mmmContext = useContext(MMMContext);
  const foldersContext = useContext(FoldersContext);
  const mmmService = new MMMService();
  const sdmmmService = new SDMMMService();
  const folderService = new FolderService();
  const { loadingDecrement, loadingIncrement, handleError } = mainContext;

  // utils
  const refreshMMMFolderTree = () => {
    foldersContext.refreshCurrentPath();
    getModels();
  };
  const handleMMMApiError = (error) => {
    switch (error.response?.status) {
      case 404: {
        refreshMMMFolderTree();
        break;
      }
      default: {
        handleError(error);
        break;
      }
    }
  };
  // snackbar
  const handlerOnSetSnackbar = (message, status) => {
    foldersContext.constructSnackbar(message, status);
  };
  const closeSnackBar = () => {
    foldersContext.refreshSnackbar();
  };
  // folder
  const createFolder = async (folderName) => {
    try {
      return await foldersContext.createNewFolder(folderName);
    } catch (error) {
      handleMMMApiError(error);
    }
  };
  const insertCurrentPath = (folderId) => {
    foldersContext.extendCurrentPath(folderId);
  };
  const updateBreadcrumbs = (index) => {
    foldersContext.updatePathToBreadcrumb(index);
  };
  const updateFavouriteStatus = async (targetItem) => {
    try {
      const toggleFavoriteParams = {
        itemID: targetItem.id,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      };

      const token = await authContext.getIdToken();
      await mmmService.toggleFavoriteStatus(token, toggleFavoriteParams);

      foldersContext.updateFolderOrItemFavoriteStatus(targetItem);
    } catch (error) {
      handleMMMApiError(error);
    }
  };
  const handlerMoveToFolder = async (targetFolder, item) => {
    try {
      loadingIncrement(MMMTYPE);
      foldersContext.moveItemToFolder(targetFolder, item);
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };
  const handlerFolderRename = async (newFolderName, targetFolder) => {
    try {
      loadingIncrement(MMMTYPE);
      foldersContext.renameFolder(newFolderName, targetFolder);
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };
  const handlerFolderDelete = async (deleteItem) => {
    try {
      loadingIncrement(MMMTYPE);
      foldersContext.removeFoldersAndReconnectChildren(deleteItem);
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const createRemoveItemsSnackbar = (
    successfullyDeletedFolders,
    successfullyDeletedModels
  ) => {
    if (successfullyDeletedFolders.length === 0) {
      let snackbarMessage;

      if (successfullyDeletedModels.length === 1) {
        const model = successfullyDeletedModels[0];
        snackbarMessage = `${model.file_name} (${model.id}) has been successfully deleted.`;
      }

      if (successfullyDeletedModels.length > 1) {
        snackbarMessage = `Selected models (${successfullyDeletedModels.length} total) have been successfully deleted.`;
      }

      foldersContext.constructSnackbar(snackbarMessage, 'success');
    }

    if (successfullyDeletedModels.length === 0) {
      let snackbarMessage;

      if (successfullyDeletedFolders.length === 1) {
        const folder = successfullyDeletedFolders[0];
        snackbarMessage = `Folder ${folder.folder_name} has been successfully deleted.`;
      }

      if (successfullyDeletedFolders.length > 1) {
        snackbarMessage = `Selected folders (${successfullyDeletedFolders.length} total) have been successfully deleted.`;
      }

      foldersContext.constructSnackbar(snackbarMessage, 'success');
    }

    if (
      successfullyDeletedModels.length >= 1 &&
      successfullyDeletedFolders.length >= 1
    ) {
      const snackbarMessage = `Selected items 
        (${
          successfullyDeletedFolders.length + successfullyDeletedModels.length
        } total) have been successfully deleted.`;

      foldersContext.constructSnackbar(snackbarMessage, 'success');
    }
  };

  /**
   * removeTreeItemsAndReparent - delete a list of specified folders and mmms
   *
   * @param {targetItems} - current MMMs and folders that are getting deleted
   *
   */
  const removeTreeItemsAndReparentChildren = async (targetItems) => {
    try {
      loadingIncrement(MMMTYPE);

      const modelsToRemove = targetItems.filter((item) => !item.isFolder);
      const foldersToRemove = targetItems.filter((item) => item.isFolder);

      const token = await authContext.getIdToken();

      let successfullyDeletedModels = [];
      let successfullyDeletedFolders = [];
      // will be reparented to this items parent
      let foundItem;

      if (modelsToRemove.length > 0) {
        const removeModelDetails = {
          models: modelsToRemove.map((model) => model.id),
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        };
        const modelData = await mmmService.removeModel(
          token,
          removeModelDetails
        );
        successfullyDeletedModels = modelsToRemove.filter((model) => {
          return modelData.model_statuses[model.id][1] === 200;
        });
        // assuming all removed items have the same parent
        foundItem = successfullyDeletedModels[0];
      }

      if (foldersToRemove.length > 0) {
        const removeFolderDetails = {
          foldersToDelete: foldersToRemove,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        };

        const folderData = await folderService.removeFolder(
          token,
          removeFolderDetails
        );
        successfullyDeletedFolders = foldersToRemove.filter((folder) => {
          return folderData.folder_statuses[folder.id][1] === 200;
        });
        // assuming all removed items have the same parent
        foundItem = successfullyDeletedFolders[0];
      }

      if (
        successfullyDeletedModels.length === 0 &&
        successfullyDeletedFolders.length === 0
      ) {
        return;
      }

      createRemoveItemsSnackbar(
        successfullyDeletedFolders,
        successfullyDeletedModels
      );

      foldersContext.updateFolderTreeWithAction(
        foundItem,
        deleteTargetItems,
        successfullyDeletedModels,
        successfullyDeletedFolders
      );
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      mmmContext.setActiveModel(false);
      loadingDecrement(MMMTYPE);
    }
  };

  // MMM
  const getModels = async () => {
    try {
      loadingIncrement(MMMTYPE);

      const modelRequestDetails = {
        countryCode: authContext.userCountryCode,
        buildTree: true,
        kwargs: mainContext.getPostKWArgs(),
      };

      const token = await authContext.getIdToken();
      const models = await mmmService.getModels(token, modelRequestDetails);

      foldersContext.setFoldersAndPath(models, mmmRootParentPath);
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const createNewMMM = async (currentPath) => {
    try {
      loadingIncrement(MMMTYPE);

      const newModelDetails = {
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
        parent: currentPath[currentPath.length - 1],
      };
      const token = await authContext.getIdToken();
      const newModelResponse = await mmmService.createNewModel(
        token,
        newModelDetails
      );

      mmmContext.setActiveModel(false);
      history.push(
        `${DASHBOARD_ROUTES.MMM}/${newModelResponse.file}/edit?mode=new`
      );
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const updateMMMMetadata = async (mmm) => {
    if (mmm.status !== 'Processed') {
      const snackbarMessage = 'MMM has not been processed';
      handlerOnSetSnackbar(snackbarMessage, 'error');
    } else {
      try {
        loadingIncrement(MMMTYPE);
        const metadataUpdateDerails = {
          file: mmm.id,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        };

        const token = await authContext.getIdToken();
        await mmmService.updateMetadataRequest(token, metadataUpdateDerails);

        const snackbarMessage = `Metadata updated for "${mmm.file_name}" (${mmm.id})`;
        handlerOnSetSnackbar(snackbarMessage, 'success');
      } catch (error) {
        handleMMMApiError(error);
      } finally {
        loadingDecrement(MMMTYPE);
      }
    }
  };

  const duplicateMMM = async (mmm, isDevCopy = false) => {
    try {
      loadingIncrement(MMMTYPE);

      const copyModelParams = {
        file: mmm.id,
        is_dev_copy: isDevCopy,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      };
      const token = await authContext.getIdToken();
      const copyModelResult = await mmmService.copyModel(
        token,
        copyModelParams
      );

      mmmContext.setActiveModel(false);
      history.push(`${DASHBOARD_ROUTES.MMM}/${copyModelResult.file}/edit`);
      return copyModelResult;
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const generateMMMConfigFile = async (mmm) => {
    try {
      loadingIncrement(MMMTYPE);

      const configDetails = {
        owner: mmm.owner,
        file: mmm.id,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      };

      const token = await authContext.getIdToken();
      await mmmService.createConfigFileForMMM(token, configDetails);

      const snackbarMessage = `Config file constructed for "${mmm.file_name}" (${mmm.id})`;
      handlerOnSetSnackbar(snackbarMessage, 'success');
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  const buildMMMRequest = async (targetModel) => {
    try {
      loadingIncrement(MMMTYPE);

      const buildRequestParams = {
        owner: targetModel.owner,
        file: targetModel.id,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      };
      const token = await authContext.getIdToken();
      await sdmmmService.buildModel(token, buildRequestParams);

      foldersContext.updateFolderTreeWithAction(
        targetModel,
        updateModelStatus,
        targetModel
      );
      const snackbarMessage = `Processing "${targetModel.file_name} (${targetModel.id})"`;
      handlerOnSetSnackbar(snackbarMessage, 'success');
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  // notes
  const getNotes = async (modelId) => {
    try {
      loadingIncrement(MMMTYPE);

      const notesRequestDetails = {
        countryCode: authContext.userCountryCode,
        file: modelId,
        kwargs: mainContext.getPostKWArgs(),
      };

      const token = await authContext.getIdToken();
      const notesResponse = await mmmService.getNotes(
        token,
        notesRequestDetails
      );
      return notesResponse.notes;
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };
  const createAndUpdateNotes = async (modelId, newNotes) => {
    try {
      loadingIncrement(MMMTYPE);

      const updatedNotesDetails = {
        countryCode: authContext.userCountryCode,
        file: modelId,
        kwargs: mainContext.getPostKWArgs(),
        notes: newNotes,
      };

      const token = await authContext.getIdToken();
      await mmmService.createOrUpdateNotes(token, updatedNotesDetails);
    } catch (error) {
      handleMMMApiError(error);
    } finally {
      loadingDecrement(MMMTYPE);
    }
  };

  return (
    <MMMListViewContext.Provider
      value={{
        mmmFolderTree: foldersContext.getFolderTree(),
        mmmCurrentPath: foldersContext.getCurrentPath(),
        mmmSnackbar: foldersContext.getSnackbar(),
        buildMMMRequest,
        closeSnackBar,
        createAndUpdateNotes,
        createFolder,
        createNewMMM,
        duplicateMMM,
        generateMMMConfigFile,
        getModels,
        getNotes,
        handlerFolderDelete,
        handlerFolderRename,
        handlerMoveToFolder,
        handlerOnSetSnackbar,
        insertCurrentPath,
        removeTreeItemsAndReparentChildren,
        updateBreadcrumbs,
        updateFavouriteStatus,
        updateMMMMetadata,
      }}
    >
      {children}
    </MMMListViewContext.Provider>
  );
};

export default MMMListViewProvider;
